// Load a screen's code only when it is first shown.
//
// Before this, every screen shipped in one main.js (8.6MB): a client opening
// the app on a phone downloaded and parsed the advisor's compliance, books,
// composer and portfolio code before anything painted. A component made with
// lazyPage() lives in its own chunk instead, fetched the first time it renders.
//
// It's a drop-in for a static import: the Suspense boundary is built in, so
// render sites don't change. The fallback is the brand loader (the boot
// splash's pulsing logo). Pass `null` for things that float (modals, pills),
// where a loader would flash in the wrong place.
//
// A chunk that fails to arrive must never take the app down with it (React
// unmounts everything above a thrown lazy load that no boundary catches, which
// is a white page). So: retry once (a network blip), and if that fails too,
// render a small "reload" note in this one spot. A 404 after a deploy is
// usually reloaded away first by chunkReload.ts.

import React, { Suspense } from "react";
import { BrandLoader } from "../components/BrandLoader";

const PAGE_FALLBACK = (
  <div style={{ minHeight: 240 }}>
    <BrandLoader />
  </div>
);

// For whole pages loaded straight off the boot splash (App.tsx routes), so the
// logo stays put instead of jumping to a smaller in-page loader.
export const FULL_SCREEN_FALLBACK = <BrandLoader fullScreen />;

function LoadFailed() {
  return (
    <div role="alert" style={{ padding: 16, fontSize: 14, color: "#374151", display: "flex", alignItems: "center", gap: 12 }}>
      <span>This section didn't load.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{ background: "#5A9E3A", color: "#fff", border: "none", borderRadius: 4, padding: "6px 12px", fontWeight: 600, cursor: "pointer" }}
      >
        Reload
      </button>
    </div>
  );
}

export type LazyPage<T extends React.ComponentType<any>> = T & { preload: () => void };

export function lazyPage<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  fallback: React.ReactNode = PAGE_FALLBACK
): LazyPage<T> {
  const Lazy = React.lazy(() =>
    factory()
      .catch(() => factory())
      .catch(() => ({ default: LoadFailed as unknown as T }))
  );
  const Wrapped = (props: React.ComponentProps<T>) => (
    <Suspense fallback={fallback}>
      <Lazy {...(props as any)} />
    </Suspense>
  );
  // Starts the download without rendering. The browser caches the chunk, so
  // the later render (and React.lazy's own import) resolves immediately.
  (Wrapped as any).preload = () => { factory().catch(() => {}); };
  return Wrapped as unknown as LazyPage<T>;
}
